import { useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  Box, Stack, Group, Title, Text, Button, Paper, Image, Loader, Alert,
  TextInput, Textarea, Progress, Badge, Anchor, List,
} from '@mantine/core';
import { casosApi } from '../../casos-api/casosApi';
import { LogosEvento } from '../Kiosk/LogosEvento';
import { CASOS_CON_IPG } from '../../utils/cvdl';
import './casos.css';
import '../Kiosk/cvdlClaro.css';

/**
 * Caso Abierto — la superficie pública.
 *
 * Las tres reglas de §6.3 que se ven acá:
 *
 *   · En ninguna pantalla, en ningún estado, aparece «incorrecto», «error» ni
 *     un puntaje. Se dice «coincidiste» o «elegiste distinto». No es un
 *     eufemismo: en clínica muchas veces no hay una única respuesta, y los
 *     comentarios del docente son matices, no claves de corrección.
 *   · El cierre NO se condiciona. Quien llegó hasta acá ya vio el caso entero,
 *     el desenlace y la devolución del docente. Los datos se piden después y
 *     como una oferta —más casos, más beneficios—, no como un peaje: retener
 *     el final arruina la buena voluntad recién construida, y quien lo deja
 *     igual se va con mal sabor.
 *   · Se puede salir y volver: la corrida se retoma donde estaba.
 */
export function CasoPage() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const modoPedido = params.get('modo') === 'full' ? 'full' : 'short';
  const ruta = params.get('source') || params.get('utm_source') || null;

  const [caso, setCaso] = useState(null);
  const [error, setError] = useState(null);
  const [runId, setRunId] = useState(null);

  const [paso, setPaso] = useState(-1);          // -1 es la portada
  const [elegidas, setElegidas] = useState({});  // stageId -> respuesta del server
  const [cierre, setCierre] = useState(null);
  const relojRef = useRef(null);

  useEffect(() => {
    let vivo = true;
    casosApi.ver(slug, modoPedido)
      .then(c => { if (vivo) setCaso(c); })
      .catch(e => { if (vivo) setError(e.status === 404 ? 'NO_EXISTE' : e.message); });
    return () => { vivo = false; };
  }, [slug, modoPedido]);

  async function empezar() {
    try {
      const r = await casosApi.arrancar(slug, {
        mode: modoPedido === 'full' ? 'FULL' : 'SHORT', route: ruta,
      });
      setRunId(r.runId);
      // Retomar donde estaba: se salta hasta la primera etapa sin contestar.
      const hechas = new Set((r.elegidas || []).map(e => String(e.stage)));
      const i = caso.etapas.findIndex(e => e.decision && !hechas.has(String(e._id)));
      setPaso(i === -1 ? caso.etapas.length - 1 : i);
      relojRef.current = Date.now();
    } catch (e) { setError(e.message); }
  }

  async function elegir(etapa, key) {
    try {
      const r = await casosApi.decidir(runId, {
        stageId: etapa._id, key,
        ms: relojRef.current ? Date.now() - relojRef.current : null,
      });
      setElegidas(prev => ({ ...prev, [etapa._id]: { ...r, elegida: key } }));
    } catch (e) { setError(e.message); }
  }

  async function avanzar() {
    if (paso < caso.etapas.length - 1) {
      setPaso(paso + 1);
      relojRef.current = Date.now();
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    try { setCierre(await casosApi.terminar(runId)); }
    catch (e) { setError(e.message); }
  }

  if (error === 'NO_EXISTE') {
    return (
      <Marco>
        <Title order={2}>Ese caso no está disponible</Title>
        <Text fz='var(--fs-body)' c="dimmed">
          Puede que todavía esté corriendo en la comunidad de WhatsApp: allá van
          primero, y a la web llegan después.
        </Text>
        <Button component="a" href="/casos" mt="md">Ver todos los casos</Button>
      </Marco>
    );
  }
  if (error) return <Marco><Alert color="red">{error}</Alert></Marco>;
  if (!caso) return <Marco><Loader /></Marco>;

  if (cierre) return <Marco><Cierre datos={cierre} runId={runId} caso={caso} /></Marco>;

  // ---- Portada (etapa 0 de §3.2) -----------------------------------------
  if (paso === -1) {
    return (
      <Marco>
        <Stack gap="lg">
          <AvisoBorrador estado={caso.borrador} />
          <Badge color='var(--color-secondary)' size="lg">{etiquetaArea(caso.specialty)}</Badge>
          <Title order={1} className="caso-titulo">{caso.title}</Title>
          {caso.paciente?.photoUrl && (
            <Image src={caso.paciente.photoUrl} radius="md"
              alt={`${caso.paciente.species} ${caso.paciente.breed}, el paciente del caso.`} />
          )}
          <Text size="lg">{caso.resena}</Text>
          <Text c="dimmed" fz='var(--fs-small)' size="sm">
            {[caso.paciente?.species, caso.paciente?.breed, caso.paciente?.sex,
            caso.paciente?.age].filter(Boolean).join(' · ')}
          </Text>

          <Button size="lg" h={56} onClick={empezar}>EMPEZAR</Button>
          {/* Lo que hace que la gente entre. Va en letra chica y es verdad. */}
          <Text ta="center" fz='var(--fs-small)' c="dimmed">
            Sin registro. {caso.etapas.filter(e => e.decision).length} decisiones,
            unos 90 segundos.
          </Text>

          <FichaAutor autor={caso.autor} />
        </Stack>
      </Marco>
    );
  }

  const etapa = caso.etapas[paso];
  const respuesta = elegidas[etapa._id];
  const decidida = Boolean(respuesta);

  return (
    <Marco>
      <Stack gap="md">
        <AvisoBorrador estado={caso.borrador} />
        <Progress value={((paso + 1) / caso.etapas.length) * 100} size="sm"
          aria-label={`Etapa ${paso + 1} de ${caso.etapas.length}`} />

        <Cuerpo texto={etapa.body} />

        {(etapa.media || []).filter(m => m.url).map((m, i) => (
          <MediaClinica key={`${etapa._id}-${i}`} m={m} />
        ))}

        {etapa.teacherNote && (
          <Paper withBorder radius="md" p="md" className="caso-pista">
            <Text fz='var(--fs-small)' fw={600} c="dimmed" mb={4}>
              {caso.autor.nombre.split(' ').slice(0, 2).join(' ')} apunta
            </Text>
            <Text fz='var(--fs-tiny)'>{etapa.teacherNote}</Text>
          </Paper>
        )}

        {etapa.decision && (
          <Stack gap="sm">
            <Title order={3} className="caso-pregunta">{etapa.decision.prompt}</Title>

            {etapa.decision.options.map(o => {
              const esta = respuesta?.elegida === o.key;
              const delDocente = decidida && respuesta.laDelDocente?.key === o.key;
              return (
                <Button
                  key={o.key}
                  variant={esta ? 'filled' : 'outline'}
                  className={`caso-opcion${delDocente ? ' es-del-docente' : ''}`}
                  disabled={decidida && !esta && !delDocente}
                  onClick={() => !decidida && elegir(etapa, o.key)}
                  h="auto" py={12}
                >
                  <Text className="caso-opcion-texto">{o.text}</Text>
                </Button>
              );
            })}

            {decidida && <Devolucion r={respuesta} opciones={etapa.decision.options} />}
          </Stack>
        )}

        {(decidida || !etapa.decision) && (
          <Button size="lg" h={52} onClick={avanzar}>
            {paso < caso.etapas.length - 1 ? 'SEGUIR' : 'VER EL CIERRE'}
          </Button>
        )}
      </Stack>
    </Marco>
  );
}

/* ---- Una pieza de media --------------------------------------------------- */
// Las imágenes duras —cadáveres, necropsias, lesiones— no se muestran solas.
// Esta página es pública y se abre desde un QR en un pasillo: quien la escanea
// no eligió ver eso, y que le aparezca de golpe en el teléfono es una falta de
// respeto con la persona y con el caso.
//
// No es censura. La imagen está, se ve entera y el alt la describe para quien
// use lector de pantalla. Lo único que cambia es quién decide cuándo.
function MediaClinica({ m }) {
  const [mostrar, setMostrar] = useState(!m.sensitive);

  if (!mostrar) {
    return (
      <Paper withBorder radius="md" p="lg" className="caso-media-tapada">
        <Stack gap="xs" align="center">
          <Text fw={600} ta="center">{m.sensitiveLabel || 'Imagen sensible'}</Text>
          {/* El alt hace de descripción: quien prefiere no mirar puede seguir
              el caso igual, sin quedarse sin el dato clínico. */}
          {m.alt && (
            <Text fz='var(--fs-small)' c="dimmed" ta="center" maw={460}>{m.alt}</Text>
          )}
          <Button variant="outline" size="sm" onClick={() => setMostrar(true)}>
            MOSTRAR LA IMAGEN
          </Button>
        </Stack>
      </Paper>
    );
  }

  return (
    <Box>
      {m.type === 'VIDEO'
        ? <video src={m.url} poster={m.posterUrl || undefined}
          autoPlay muted loop playsInline preload="metadata"
          className="caso-video" aria-label={m.alt} />
        : <Image src={m.url} radius="md" alt={m.alt} />}
      {m.caption && <Text fz='var(--fs-small)' size="sm" c="dimmed" mt={4}>{m.caption}</Text>}
    </Box>
  );
}

/* ---- Devolución de una decisión ----------------------------------------- */
function Devolucion({ r, opciones }) {
  return (
    <Paper withBorder radius="md" p="md" className="caso-devolucion">
      {/* Nunca "correcto" ni "incorrecto": coincidiste o elegiste distinto. */}
      <Text fz='var(--fs-body)' fw={700} mb={6}>
        {r.coincidiste ? 'Coincidiste con el docente' : 'Elegiste distinto al docente'}
      </Text>

      <Text fz='var(--fs-small)' mb={r.coincidiste ? 0 : 10}>{r.feedback}</Text>

      {!r.coincidiste && r.laDelDocente && (
        <Box className="caso-la-del-docente">
          <Text size="sm" fw={600} mb={2}>Él eligió: {r.laDelDocente.text}</Text>
          <Text size="sm">{r.laDelDocente.feedback}</Text>
        </Box>
      )}

      {/* §6.3: la barra recién con 20 corridas. Antes de eso no se insinúa
          siquiera cuántas van: sería una métrica de vanidad al revés. */}
      {r.distribucion && (
        <Stack gap={6} mt="md">
          <Text size="sm" c="dimmed">Qué eligieron los demás colegas</Text>
          {r.distribucion.map(d => {
            const o = opciones.find(x => x.key === d.key);
            return (
              <Box key={d.key}>
                <Group justify="space-between" gap="xs">
                  <Text size="sm" className="caso-barra-texto">{o?.text}</Text>
                  <Text size="sm" fw={600}>{d.porcentaje}%</Text>
                </Group>
                <Progress value={d.porcentaje} size="sm"
                  aria-label={`${o?.text}: ${d.porcentaje} por ciento`} />
              </Box>
            );
          })}
        </Stack>
      )}
    </Paper>
  );
}

/* ---- Cierre -------------------------------------------------------------- */
// El caso ya está entero arriba: acá no se retiene nada. Primero el resultado
// y quién lo aportó, y recién después la oferta de dejar los datos. El orden
// importa: pedirlos antes convierte el cierre en un peaje.
//
// No hay premio ni chance. El cierre prometía las dos cosas y ninguna existía:
// nada emitía un código que el equipo del stand pudiera verificar, y nada leía
// las chances. Mandaba gente al mostrador a reclamar algo que no había.
function Cierre({ datos, runId, caso }) {
  const [email, setEmail] = useState('');
  const [telefono, setTelefono] = useState('');
  const [nombre, setNombre] = useState('');
  const [texto, setTexto] = useState('');
  const [error, setError] = useState(null);
  const [dejado, setDejado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const [enviado, setEnviado] = useState(false);

  // Alcanza con UNO de los dos: sin ninguno no hay a dónde mandarle nada, y
  // exigir los dos espanta a quien no quiere dar el teléfono.
  const mailOk = /^\S+@\S+\.\S+$/.test(email.trim());
  const telOk = telefono.replace(/\D/g, '').length >= 8;
  const puedeEnviar = mailOk || telOk;

  async function suscribir() {
    setEnviando(true); setError(null);
    try {
      await casosApi.identificar(runId, {
        email: email.trim() || undefined,
        phone: telefono.trim() || undefined,
        name: nombre.trim() || undefined,
      });
      setDejado(true);
    } catch (err) {
      setError(err.data?.error === 'EMAIL_INVALIDO' ? 'Revisá el email.'
        : err.data?.error === 'TELEFONO_INVALIDO' ? 'Revisá el número de WhatsApp.'
          : 'No pudimos guardarlo. Probá de nuevo.');
    } finally { setEnviando(false); }
  }

  return (
    <Stack gap="lg">
      <Title order={1} className="caso-titulo">{datos.resumen}</Title>

      <Text size="lg">Gracias por resolverlo. Ya viste el caso completo.</Text>

      <FichaAutor autor={caso.autor} />

      {!dejado ? (
        <Paper withBorder radius="md" p="md">
          <Text fw={700} size="lg" mb={4}>¿Te mandamos el próximo?</Text>
          <Text size="sm" c="dimmed" mb="sm">
            Dejás por dónde llegarte y entrás a la lista de casos. Nada de esto
            condiciona lo que ya viste.
          </Text>

          <List size="sm" spacing={4} mb="md">
            <List.Item>El comentado en video de este caso, con el razonamiento completo.</List.Item>
            <List.Item>Un caso nuevo por mes, antes de que salga a la web.</List.Item>
            <List.Item>Los avisos de becas y cursos de FORVET, primero por mail.</List.Item>
          </List>

          <Stack gap="sm">
            <TextInput
              type="email" label="Email" placeholder="tu@email.com"
              value={email} onChange={e => { setEmail(e.currentTarget.value); setError(null); }}
            />
            <TextInput
              inputMode="tel" label="WhatsApp" placeholder="351 123 4567"
              description="Si es del exterior, escribilo con + y el código de país"
              value={telefono}
              onChange={(e) => {
                const crudo = e.currentTarget.value;
                const internacional = crudo.trimStart().startsWith('+');
                setTelefono((internacional ? '+' : '') + crudo.replace(/\D/g, ''));
                setError(null);
              }}
            />
            <TextInput
              label="Nombre (opcional)" placeholder="Cómo te llamamos"
              value={nombre} onChange={e => setNombre(e.currentTarget.value)}
            />

            <Text size="xs" c="dimmed">
              Con uno de los dos alcanza. El nombre es para no escribirte “Hola”.
            </Text>

            {error && <Alert color="red" radius="md">{error}</Alert>}

            <Group gap="sm">
              <Button loading={enviando} disabled={!puedeEnviar} onClick={suscribir}>
                QUIERO RECIBIRLOS
              </Button>
              {/* No es un link escondido: quien no quiere dejar nada ya vio todo. */}
              <Button variant="subtle" onClick={() => setDejado(true)}>Ahora no</Button>
            </Group>
          </Stack>
        </Paper>
      ) : (
        <Alert color="green" radius="md">Listo. Gracias por jugarlo.</Alert>
      )}

      <Paper withBorder radius="md" p="md">
        <Text fw={600} mb={4}>¿Qué hubieras hecho distinto?</Text>
        <Text size="sm" c="dimmed" mb="sm">
          El docente elige una respuesta destacada por mes, y esa persona gana una
          beca completa. Se premia el razonamiento, no el acierto.
        </Text>
        <Textarea minRows={3} autosize value={texto}
                  styles={{input:{
                    backgroundColor:'transparent'
                  }}}
                  onChange={e => setTexto(e.currentTarget.value)}
                  placeholder="Contanos por qué elegiste lo que elegiste…" />
        <Button mt="sm" disabled={texto.trim().length < 10 || enviado}
                onClick={async () => {
                  await casosApi.responder(runId, texto);
                  setEnviado(true);
                }}>
          {enviado ? 'ENVIADA' : 'ENVIAR MI RESPUESTA'}
        </Button>
      </Paper>

      <Button component="a" href="/casos" variant="outline" size="lg" h={52}>
        VER OTRO CASO
      </Button>
    </Stack>
  );
}

/* ---- El autor, con su perfil --------------------------------------------- */
// El link al CV sale del slugName del usuario, no de una URL escrita a mano:
// si el docente cambia su slug, el caso lo sigue. Cuando no hay slug —alguien
// de la comunidad sin usuario en la plataforma— el nombre va en texto plano en
// lugar de a un link roto.
function FichaAutor({ autor }) {
  if (!autor?.nombre) return null;
  const perfil = autor.slug ? `/profesores/${autor.slug}` : null;

  return (
    <Paper withBorder radius="md" p="md" className="caso-autor">
      <Text size="sm" c="dimmed">Caso aportado por</Text>
      {perfil
        ? <Anchor href={perfil} fw={600} fz="lg">{autor.nombre}</Anchor>
        : <Text fw={600} fz="lg">{autor.nombre}</Text>}
      {autor.credencial && <Text size="sm" c="dimmed">{autor.credencial}</Text>}
      {autor.clinica && <Text size="sm" c="dimmed">{autor.clinica}</Text>}
      {perfil && (
        <Anchor href={perfil} size="sm" display="block" mt={6}>
          Ver su perfil y sus cursos →
        </Anchor>
      )}
    </Paper>
  );
}

/* ---- Vista previa -------------------------------------------------------- */
// Si esto se ve, el caso NO es público todavía: llegaste con el token de
// dirección. Lo que se juega acá no cuenta para las estadísticas ni para la
// distribución de respuestas.
function AvisoBorrador({ estado }) {
  if (!estado) return null;
  const texto = estado === 'COMMUNITY_ONLY'
    ? 'Este caso está corriendo en la comunidad. Todavía no es público en la web.'
    : 'Borrador. Este caso todavía no está publicado.';
  return (
    <Alert color="orange" radius="md">
      <Text fw={700}>{texto}</Text>
      <Text size="sm">
        Lo estás viendo con el token de dirección. Tus respuestas no suman a las
        estadísticas del caso.
      </Text>
    </Alert>
  );
}

/* ---- Utilidades ---------------------------------------------------------- */
// El encabezado va en el Marco y no en cada pantalla: así aparece igual en la
// portada, en cada etapa y en el cierre, y también en el 404. Chico a propósito
// —26 px— porque abajo hay texto clínico para leer, no una home para mirar.
//
// Las dos marcas son temporales: ver CASOS_CON_IPG en utils/cvdl.js.
const Marco = ({ children }) => (
  <Box className="cvdl-claro caso-marco">
    <Box className="caso-ancho">
      <LogosEvento h={26} conIpg={CASOS_CON_IPG} c="var(--mantine-color-forvet-8)" mb="xl" />
      {children}
    </Box>
  </Box>
);

// El body viene en markdown simple: negritas y viñetas. No traemos una
// librería entera para eso — son dos reemplazos y el peso importa (§6.4).
function Cuerpo({ texto }) {
  if (!texto) return null;
  return (
    <Stack gap="xs">
      {texto.split('\n').filter(l => l.trim()).map((linea, i) => {
        const partes = linea.split(/\*\*(.+?)\*\*/g);
        const contenido = partes.map((p, j) =>
          j % 2 ? <strong key={j}>{p}</strong> : p);
        return linea.trim().startsWith('- ')
          ? <Text fz='var(--fs-body)' key={i} className="caso-vineta">{contenido}</Text>
          : <Text fz='var(--fs-body)' key={i}>{contenido}</Text>;
      })}
    </Stack>
  );
}

const AREAS = {
  MEDICINA_FELINA: 'Medicina felina', MEDICINA_INTERNA: 'Medicina interna',
  CIRUGIA: 'Cirugía', IMAGENES: 'Imágenes', CARDIOLOGIA: 'Cardiología',
  DERMATOLOGIA: 'Dermatología', ONCOLOGIA: 'Oncología', EMERGENCIAS: 'Emergencias',
  ANESTESIOLOGIA: 'Anestesiología', NUTRICION: 'Nutrición', FISIOTERAPIA: 'Fisioterapia',
  NEUROLOGIA: 'Neurología', EXOTICOS: 'Exóticos', GRANDES_ANIMALES: 'Grandes animales',
  GESTION: 'Gestión de la clínica', OTRA: 'Otra',
};
const etiquetaArea = a => AREAS[a] || a;
